// WebGL wave field for the landing page — adapted from Boris Šehovac's
// shader-program "waves" demo (github.com/bsehovac/shader-program), rebuilt
// as zero-dependency raw WebGL on the SeaState palette: a true-perspective
// plane of soft navy points rolling with a diagonal cos(x)+sin(z) swell,
// receding to a quiet horizon. The soft sprite is procedural (smoothstep
// falloff) instead of his texture, so nothing loads. All motion lives in the
// vertex shader; honors prefers-reduced-motion with a single still frame.

const PLANE_DEPTH = 400; // world units, camera at origin looking down -z
const NEAREST_Z = 30; //   closest row (caps foreground point size)
const PLANE_Y = -30; //    sea level below the camera
const GRID_STEP = 5;
const POINT_SIZE = 2.5;
const SPEED = 1.3; //      rad/s — gently rolling

const VERT = `
#define M_PI 3.1415926535897932384626433832795
precision highp float;

attribute vec2 aCell;          // world x,z of this point on the plane
uniform mat4 uProj;
uniform float uTime;
uniform float uSize;
uniform vec3 uField;           // width, height(amplitude), depth
uniform vec2 uMouse;           // smoothed cursor in [-1,1]², 0 at rest
varying float vAlpha;

void main() {
  vec3 pos = vec3(aCell.x, ${PLANE_Y.toFixed(1)}, aCell.y);

  float phase = cos(pos.x / uField.x * M_PI * 8.0 + uTime * ${SPEED.toFixed(2)})
              + sin(pos.z / uField.z * M_PI * 8.0 + uTime * ${SPEED.toFixed(2)});
  pos.y += phase * uField.y;

  // whisper parallax: the cursor shears the plane, more with distance
  pos.x += uMouse.x * pos.z * 0.05;
  pos.y += uMouse.y * pos.z * 0.018;

  vec4 clip = uProj * vec4(pos, 1.0);
  gl_Position = clip;
  gl_PointSize = (uSize / clip.w) * 100.0;

  // 0 at the far edge, 1 at the nearest row
  float depthFrac = clamp(
    (pos.z + uField.z) / (uField.z - ${NEAREST_Z.toFixed(1)}), 0.0, 1.0);
  float crest = phase * 0.25 + 0.5;
  vAlpha = mix(0.05, 0.4, depthFrac) * (0.6 + 0.5 * crest);
}`;

const FRAG = `
precision mediump float;
uniform vec3 uColor;
varying float vAlpha;
void main() {
  float d = length(gl_PointCoord - vec2(0.5));
  float soft = smoothstep(0.5, 0.08, d);   // procedural soft sprite
  gl_FragColor = vec4(uColor, vAlpha * soft);
}`;

function perspective(fovDeg: number, aspect: number, near: number, far: number): Float32Array {
  const f = 1 / Math.tan((fovDeg * Math.PI) / 360);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  if (!Number.isFinite(n)) return [0.086, 0.137, 0.18];
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const sh = gl.createShader(type);
  if (sh === null) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (gl.getShaderParameter(sh, gl.COMPILE_STATUS) !== true) {
    console.warn("waves: shader compile failed:", gl.getShaderInfoLog(sh));
    return null;
  }
  return sh;
}

/**
 * Mount the wave plane into `container` (position:relative). Returns true when
 * WebGL is running; false when unavailable (caller may fall back to a static
 * texture). The animation stops itself when the canvas leaves the document.
 */
export function waveField(container: HTMLElement): boolean {
  const canvas = document.createElement("canvas");
  canvas.className = "waves-bg";
  container.appendChild(canvas);

  const gl = canvas.getContext("webgl", {
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: false,
  });
  if (gl === null) {
    canvas.remove();
    return false;
  }

  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
  const prog = gl.createProgram();
  if (vs === null || fs === null || prog === null) {
    canvas.remove();
    return false;
  }
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (gl.getProgramParameter(prog, gl.LINK_STATUS) !== true) {
    console.warn("waves: link failed:", gl.getProgramInfoLog(prog));
    canvas.remove();
    return false;
  }
  gl.useProgram(prog);

  const aCell = gl.getAttribLocation(prog, "aCell");
  const uProj = gl.getUniformLocation(prog, "uProj");
  const uTime = gl.getUniformLocation(prog, "uTime");
  const uSize = gl.getUniformLocation(prog, "uSize");
  const uField = gl.getUniformLocation(prog, "uField");
  const uColor = gl.getUniformLocation(prog, "uColor");
  const uMouse = gl.getUniformLocation(prog, "uMouse");

  const navy = getComputedStyle(document.documentElement).getPropertyValue("--navy");
  gl.uniform3fv(uColor, hexToRgb(navy !== "" ? navy : "#16232e"));

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(0, 0, 0, 0);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.enableVertexAttribArray(aCell);
  gl.vertexAttribPointer(aCell, 2, gl.FLOAT, false, 0, 0);

  let count = 0;

  function resize(): void {
    if (gl === null) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w <= 0 || h <= 0) return;
    const dpr = Math.min(window.devicePixelRatio > 0 ? window.devicePixelRatio : 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    gl.viewport(0, 0, canvas.width, canvas.height);

    const aspect = w / h;
    gl.uniformMatrix4fv(uProj, false, perspective(60, aspect, 1, 1000));

    // plane widens with the viewport, like the original
    const width = PLANE_DEPTH * aspect;
    gl.uniform3f(uField, width, 3, PLANE_DEPTH);
    gl.uniform1f(uSize, (h / 400) * POINT_SIZE * dpr);

    const cells: number[] = [];
    for (let x = 0; x < width; x += GRID_STEP) {
      for (let z = 0; z < PLANE_DEPTH - NEAREST_Z; z += GRID_STEP) {
        cells.push(-width / 2 + x, -PLANE_DEPTH + z);
      }
    }
    count = cells.length / 2;
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(cells), gl.STATIC_DRAW);
  }

  // cursor parallax: ease toward the pointer so the plane drifts, not snaps
  let targetX = 0;
  let targetY = 0;
  let mouseX = 0;
  let mouseY = 0;
  const onPointerMove = (e: PointerEvent): void => {
    targetX = (e.clientX / window.innerWidth) * 2 - 1;
    targetY = (e.clientY / window.innerHeight) * 2 - 1;
  };
  const onPointerLeave = (): void => {
    targetX = 0;
    targetY = 0;
  };
  window.addEventListener("pointermove", onPointerMove);
  document.documentElement.addEventListener("pointerleave", onPointerLeave);
  const removeListeners = (): void => {
    window.removeEventListener("pointermove", onPointerMove);
    document.documentElement.removeEventListener("pointerleave", onPointerLeave);
  };

  function frame(now: number): void {
    if (gl === null) return;
    mouseX += (targetX - mouseX) * 0.035;
    mouseY += (targetY - mouseY) * 0.035;
    gl.uniform2f(uMouse, mouseX, mouseY);
    gl.uniform1f(uTime, now * 0.001);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.POINTS, 0, count);
  }

  const ro = new ResizeObserver(() => {
    resize();
    frame(performance.now());
  });
  ro.observe(container);
  resize();

  const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (still) {
    removeListeners(); // one still frame, no motion and no parallax
    frame(0);
    return true;
  }

  const loop = (now: number): void => {
    if (!canvas.isConnected) {
      ro.disconnect();
      removeListeners();
      return; // page navigated away
    }
    frame(now);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
  return true;
}
